import { DashboardWrapper } from '@/components/DashboardWrapper';
import PageContainer from '@/components/PageContainer';
import { IUserContext, useUserContext } from '@/context/UserContext';
import { Client } from '@types';
import { useEffect, useMemo, useState } from 'react';
import { get } from '@/utils/queries';
import {
  generateAmoebaUrlWithToken,
  generateChatbotNewUrlWithoutKey,
} from '@/pages/Chatbot/utils';
import { alertErrorMessage } from '@/utils/alerts';
import { Spinner } from '@/components/Spinner';

export function Chatbot() {
  const { auth, impersonatingAccountId, companyName }: IUserContext =
    useUserContext(); // Use the context
  const { data: companyData } = auth?.isClient
    ? get<Client[]>(`account/${auth.clients[0].id}`)
    : get<Client[]>('account/list');
  const [src, setSrc] = useState('');
  const [css, setCss] = useState('');
  const [isLoading, setIsLoading] = useState(true);

  const clientTeams = useMemo(() => {
    const isPreProductionEnv =
      import.meta.env?.VITE_ENV === 'local' ||
      import.meta.env?.VITE_ENV === 'test' ||
      process.env?.VITE_ENV === 'local' ||
      process.env?.VITE_ENV === 'test';
    if (isPreProductionEnv) {
      return import.meta.env.VITE_AMOEBA_TEAM_ID as string;
    }
    if (auth?.isClient) {
      return auth?.clients?.[0]?.ms_team_id;
    }
    if (impersonatingAccountId) {
      const client = companyData?.find((c) => c.id === impersonatingAccountId);
      return client?.ms_team_id;
    }
    return '';
  }, [auth, impersonatingAccountId, companyData]);

  const props = {
    ...(companyName && { companyName }),
    ...(companyData && { companyData }),
  };

  useEffect(() => {
    (async () => {
      const url = await generateAmoebaUrlWithToken([clientTeams as string]);
      setSrc(url);
      setCss('hidden');
    })().catch(() => {
      alertErrorMessage(
        'Error loading chatbot, please refresh the page or contact support.'
      );
    });
  }, [clientTeams]);

  useEffect(() => {
    return () => {
      document.cookie =
        'auth_token=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
    };
  }, []);

  return (
    <PageContainer name='files'>
      <DashboardWrapper {...props}>
        <div />
        {isLoading ? (
          <Spinner className='opacity-50 mt-20 w-[30%] justify-self-center' />
        ) : null}
        <iframe
          title='Embedded chatbox'
          className={css}
          src={src}
          // eslint-disable-next-line react/no-unknown-property
          onLoadedData={() => setSrc(generateChatbotNewUrlWithoutKey())}
          onLoad={() => {
            setCss('w-full h-full bg-transparent border-none');
            setIsLoading(false);
          }}
        />
      </DashboardWrapper>
    </PageContainer>
  );
}
